import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, Sun, Settings, User, LogOut, LogIn, Search, BookOpen, Info, FileText } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useDarkMode } from "../contexts/DarkModeContext";
import AboutModal from "./AboutModal";
import NewsSection from "./NewsSection";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [volumes, setVolumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [newsConfig, setNewsConfig] = useState(null);
  const [imageConfig, setImageConfig] = useState(null);
  const [evangelioButtonEnabled, setEvangelioButtonEnabled] = useState(true);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, userType, userInfo, logout, loading: authLoading } = useAuth();
  const { isDarkMode: darkMode, toggleDarkMode } = useDarkMode();

  // Function to handle Evangelio del Día button click - Navigate to dedicated page
  const handleEvangelioDelDia = () => {
    navigate('/evangelio');
  };

  // Fetch volumes from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch volumes, news config, image config, and evangelio button config in parallel
        const [volumesResponse, newsResponse, imageResponse, evangelioButtonResponse] = await Promise.all([
          axios.get(`${API}/volumes`),
          axios.get(`${API}/news-config`),
          axios.get(`${API}/image-config`),
          axios.get(`${API}/evangelio-button-config`)
        ]);
        
        setVolumes(volumesResponse.data.volumes);
        setNewsConfig(newsResponse.data);
        setImageConfig(imageResponse.data.featuredImage);
        setEvangelioButtonEnabled(evangelioButtonResponse.data.enabled);
        setError(null);
      } catch (err) {
        console.error("Error fetching data:", err);
        if (err.response?.status === 401) {
          setError("Sesión expirada. Redirigiendo al login...");
        } else {
          setError("Error loading data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle volume click
  const handleVolumeClick = (volume) => {
    // Consider volume available if it has YouTube URL OR playlist data
    const isEffectivelyAvailable = volume.is_available || volume.has_playlist_data;
    
    if (isEffectivelyAvailable) {
      if (volume.has_playlist_data) {
        // Check if user is logged in for detailed content
        if (isAuthenticated) {
          navigate(`/volume/${volume.volume_number}`);
        } else {
          // Redirect to login for detailed content
          navigate("/user-login");
        }
      } else if (volume.is_available && volume.youtube_url) {
        // Open YouTube playlist directly
        window.open(volume.youtube_url, '_blank');
      }
    }
  };

  // Handle global search
  const handleGlobalSearch = () => {
    setSearchError("");
    setSearchLoading(true);
    
    if (!searchInput.trim()) {
      setSearchError("Por favor ingresa un formato válido (ej: 11-5)");
      setSearchLoading(false);
      return;
    }
    
    // Parse the input format "Volume-Index"
    const parts = searchInput.trim().split('-');
    if (parts.length !== 2) {
      setSearchError("Formato inválido. Usa: Volumen-Indice (ej: 11-5)");
      setSearchLoading(false);
      return;
    }
    
    const targetVolume = parseInt(parts[0]);
    const targetIndex = parseInt(parts[1]);
    
    if (isNaN(targetVolume) || isNaN(targetIndex)) {
      setSearchError("Ingresa números válidos (ej: 11-5)");
      setSearchLoading(false);
      return;
    }
    
    // Validate volume range
    if (targetVolume < 1 || targetVolume > 36) {
      setSearchError("El volumen debe estar entre 1 y 36");
      setSearchLoading(false);
      return;
    }
    
    // Validate index is positive
    if (targetIndex <= 0) {
      setSearchError("El índice debe ser un número positivo");
      setSearchLoading(false);
      return;
    }
    
    // Check if user is authenticated for detailed content
    if (!isAuthenticated) {
      setSearchError("Debes iniciar sesión para acceder al contenido detallado");
      setSearchLoading(false);
      setTimeout(() => {
        navigate("/user-login");
      }, 1500);
      return;
    }
    
    // Check if the target volume exists and has content
    const targetVolumeData = volumes.find(v => v.volume_number === targetVolume);
    if (!targetVolumeData) {
      setSearchError(`Volumen ${targetVolume} no encontrado`);
      setSearchLoading(false);
      return;
    }
    
    // Check if volume has playlist data
    if (!targetVolumeData.has_playlist_data && !targetVolumeData.is_available) {
      setSearchError(`Volumen ${targetVolume} no tiene contenido disponible aún`);
      setSearchLoading(false);
      return;
    }
    
    // All validations passed - navigate with loading state
    try {
      navigate(`/volume/${targetVolume}`, { 
        state: { searchIndex: targetIndex } 
      });
    } catch (error) {
      setSearchError("Error al navegar. Por favor intenta de nuevo.");
      setSearchLoading(false);
    }
  };

  // Handle Enter key in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGlobalSearch();
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`${
        darkMode ? 'bg-purple-800' : 'bg-purple-600'
      } text-white py-12 transition-colors duration-300`}>
        <div className="container mx-auto px-4">
          {/* Desktop layout */}
          <div className="hidden md:block">
            {/* Top bar with title and controls */}
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold">
                Libro de Cielo
              </h1>
              <div className="flex items-center space-x-4">
                {/* User Authentication Section */}
                {isAuthenticated ? (
                  <div className="flex items-center space-x-4">
                    {userInfo && (
                      <div className="text-right">
                        <p className="text-sm opacity-90">
                          {userInfo.first_name} {userInfo.last_name}
                        </p>
                        <p className="text-xs opacity-75">{userInfo.email}</p>
                      </div>
                    )}
                    <button
                      onClick={handleLogout}
                      className={`p-3 rounded-full transition-all duration-300 ${
                        darkMode 
                          ? 'bg-purple-700 hover:bg-purple-600' 
                          : 'bg-purple-700 hover:bg-purple-800'
                      }`}
                      aria-label="Logout"
                    >
                      <LogOut size={24} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Link
                      to="/register"
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                        darkMode 
                          ? 'bg-purple-700 hover:bg-purple-600' 
                          : 'bg-purple-700 hover:bg-purple-800'
                      }`}
                    >
                      Registrarse
                    </Link>
                    <Link
                      to="/user-login"
                      className={`p-3 rounded-full transition-all duration-300 ${
                        darkMode 
                          ? 'bg-purple-700 hover:bg-purple-600' 
                          : 'bg-purple-700 hover:bg-purple-800'
                      }`}
                      aria-label="User login"
                    >
                      <LogIn size={24} />
                    </Link>
                  </div>
                )}
                
                {/* Admin Access */}
                {userType === "admin" && (
                  <Link
                    to="/admin"
                    className={`p-3 rounded-full transition-all duration-300 ${
                      darkMode 
                        ? 'bg-purple-700 hover:bg-purple-600' 
                        : 'bg-purple-700 hover:bg-purple-800'
                    }`}
                    aria-label="Admin settings"
                  >
                    <Settings size={24} />
                  </Link>
                )}
                
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className={`p-3 rounded-full transition-all duration-300 ${
                    darkMode 
                      ? 'bg-purple-700 hover:bg-purple-600' 
                      : 'bg-purple-700 hover:bg-purple-800'
                  }`}
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? <Sun size={24} /> : <Moon size={24} />}
                </button>
                
                {/* About Button */}
                <button
                  onClick={() => setAboutModalOpen(true)}
                  className={`p-3 rounded-full transition-all duration-300 ${
                    darkMode 
                      ? 'bg-purple-700 hover:bg-purple-600' 
                      : 'bg-purple-700 hover:bg-purple-800'
                  }`}
                  aria-label="About"
                >
                  <Info size={24} />
                </button>
              </div>
            </div>

            {/* Centered full width description for desktop */}
            <div className="w-full max-w-5xl mx-auto px-4">
              <p className="text-xl md:text-2xl opacity-90 text-center leading-relaxed">
                Explora los 36 volúmenes del Libro de Cielo escritos por la Sierva de Dios Luisa Piccarreta, donde se revela el don de vivir en la Divina Voluntad.
              </p>
            </div>
          </div>

          {/* Mobile layout */}
          <div className="md:hidden">
            {/* Top bar with title and controls */}
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-3xl font-bold">
                Libro de Cielo
              </h1>
              <div className="flex items-center space-x-2">
                {isAuthenticated ? (
                  <button
                    onClick={handleLogout}
                    className={`p-2 rounded-full transition-all duration-300 ${
                      darkMode 
                        ? 'bg-purple-700 hover:bg-purple-600' 
                        : 'bg-purple-700 hover:bg-purple-800'
                    }`}
                    aria-label="Logout"
                  >
                    <LogOut size={20} />
                  </button>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 ${
                        darkMode 
                          ? 'bg-purple-700 hover:bg-purple-600' 
                          : 'bg-purple-700 hover:bg-purple-800'
                      }`}
                    >
                      Registro
                    </Link>
                    <Link
                      to="/user-login"
                      className={`p-2 rounded-full transition-all duration-300 ${
                        darkMode 
                          ? 'bg-purple-700 hover:bg-purple-600' 
                          : 'bg-purple-700 hover:bg-purple-800'
                      }`}
                      aria-label="User login"
                    >
                      <LogIn size={20} />
                    </Link>
                  </>
                )}
                
                {userType === "admin" && (
                  <Link
                    to="/admin"
                    className={`p-2 rounded-full transition-all duration-300 ${
                      darkMode 
                        ? 'bg-purple-700 hover:bg-purple-600' 
                        : 'bg-purple-700 hover:bg-purple-800'
                    }`}
                    aria-label="Admin settings"
                  >
                    <Settings size={20} />
                  </Link>
                )}
                
                <button
                  onClick={toggleDarkMode}
                  className={`p-2 rounded-full transition-all duration-300 ${
                    darkMode 
                      ? 'bg-purple-700 hover:bg-purple-600' 
                      : 'bg-purple-700 hover:bg-purple-800'
                  }`}
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                
                <button
                  onClick={() => setAboutModalOpen(true)}
                  className={`p-2 rounded-full transition-all duration-300 ${
                    darkMode 
                      ? 'bg-purple-700 hover:bg-purple-600' 
                      : 'bg-purple-700 hover:bg-purple-800'
                  }`}
                  aria-label="About"
                >
                  <Info size={20} />
                </button>
              </div>
            </div>

            {/* Full width description for mobile */}
            <div className="w-full px-2">
              <p className="text-lg opacity-90 text-center leading-relaxed">
                Explora los 36 volúmenes del Libro de Cielo escritos por la Sierva de Dios Luisa Piccarreta, donde se revela el don de vivir en la Divina Voluntad.
              </p>
            </div>

            {/* User info for mobile if authenticated */}
            {isAuthenticated && userInfo && (
              <div className="mt-4 text-center">
                <p className="text-sm opacity-90">
                  {userInfo.first_name} {userInfo.last_name}
                </p>
                <p className="text-xs opacity-75">{userInfo.email}</p>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">

        {/* News Section */}
        <NewsSection 
          publicNews={newsConfig?.public}
          privateNews={newsConfig?.private}
          isAuthenticated={isAuthenticated}
          userInfo={userInfo}
          darkMode={darkMode}
        />

        {/* User Notice for Detailed Content */}
        {!isAuthenticated && (
          <div className={`max-w-4xl mx-auto mb-8 p-4 rounded-lg border-l-4 border-purple-500 ${
            darkMode ? 'bg-gray-800' : 'bg-purple-50'
          }`}>
            <p className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
              💡 <strong>¿Sabías que?</strong> Al crear una cuenta podrás acceder a contenido detallado de los volúmenes, 
              marcar videos como vistos y tener un seguimiento personalizado de tu progreso.
            </p>
          </div>
        )}

        {/* Featured Image */}
        {imageConfig && (
          <div className="max-w-4xl mx-auto mb-12 text-center">
            <img
              src={imageConfig.imageUrl}
              alt={imageConfig.altText}
              title={imageConfig.tooltipText}
              onClick={() => window.open(imageConfig.clickUrl, '_blank')}
              className={`
                cursor-pointer mx-auto rounded-lg shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl
                w-full md:w-3/4 h-auto max-w-full object-contain
              `}
              style={{
                maxHeight: '400px'
              }}
            />
          </div>
        )}

        {/* Evangelio del Día Button - Only show if enabled */}
        {evangelioButtonEnabled && (
          <div className="max-w-4xl mx-auto mb-8 text-center">
            <button
              onClick={handleEvangelioDelDia}
              className={`inline-flex items-center px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
                darkMode 
                  ? 'bg-green-700 hover:bg-green-600 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <BookOpen size={24} className="mr-3" />
              Evangelio del Día
            </button>
            <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Lee la lectura del evangelio de hoy
            </p>
          </div>
        )}

        {/* Explorar Documentos Button */}
        <div className="max-w-4xl mx-auto mb-8 text-center">
          <button
            onClick={() => navigate('/documentos')}
            className={`inline-flex items-center px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
              darkMode 
                ? 'bg-blue-700 hover:bg-blue-600 text-white' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <FileText size={24} className="mr-3" />
            Explorar Documentos
          </button>
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Accede a guías, recursos y documentos especiales
          </p>
        </div>

        {/* Temas Button - Available for all users */}
        <div className="max-w-4xl mx-auto mb-8 text-center">
          <button
            onClick={() => {
              if (isAuthenticated) {
                navigate('/temas');
              } else {
                navigate('/user-login');
              }
            }}
            className={`inline-flex items-center px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
              darkMode 
                ? 'bg-purple-700 hover:bg-purple-600 text-white' 
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            <BookOpen size={24} className="mr-3" />
            Explorar Temas
          </button>
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {isAuthenticated 
              ? 'Descubre temas organizados del Libro de Cielo'
              : 'Inicia sesión para descubrir temas organizados del Libro de Cielo'
            }
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando volúmenes...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Volumes Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {volumes.map((volume) => {
              // Consider volume available if it has YouTube URL OR playlist data
              const isEffectivelyAvailable = volume.is_available || volume.has_playlist_data;
              
              return (
                <div
                  key={volume.id}
                  onClick={() => handleVolumeClick(volume)}
                  className={`relative rounded-lg p-6 text-center transition-all duration-300 transform hover:scale-105 ${
                    isEffectivelyAvailable
                      ? `cursor-pointer border-2 border-blue-500 ${
                          darkMode 
                            ? 'bg-gray-800 hover:bg-gray-700 shadow-lg hover:shadow-xl' 
                            : 'bg-white hover:bg-gray-50 shadow-md hover:shadow-lg'
                        }`
                      : `cursor-default ${
                          darkMode 
                            ? 'bg-gray-800 border border-gray-700' 
                            : 'bg-white border border-gray-200'
                        }`
                  }`}
                >
                  {/* Status Indicator */}
                  <div className="absolute top-3 right-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        isEffectivelyAvailable ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    />
                  </div>

                  {/* Enhanced indicator for volumes with playlist data */}
                  {volume.has_playlist_data && (
                    <div className="absolute top-3 left-3">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" title="Contenido detallado disponible" />
                    </div>
                  )}

                  {/* Volume Content */}
                  <div className="mt-4">
                    <h3 className={`text-xl font-bold mb-2 ${
                      isEffectivelyAvailable ? 'text-purple-600' : 'text-gray-500'
                    }`}>
                      {volume.title}
                    </h3>
                    <p className={`text-sm ${
                      isEffectivelyAvailable
                        ? 'text-green-600 font-medium'
                        : 'text-gray-500'
                    }`}>
                      {isEffectivelyAvailable ? "Disponible" : "No disponible"}
                    </p>
                    {volume.has_playlist_data && (
                      <p className="text-xs text-yellow-600 mt-1">
                        {isAuthenticated ? "✨ Contenido detallado" : "🔒 Requiere cuenta"}
                      </p>
                    )}
                  </div>

                  {/* Hover effect for available volumes */}
                  {isEffectivelyAvailable && (
                    <div className="absolute inset-0 bg-purple-600 bg-opacity-0 hover:bg-opacity-10 rounded-lg transition-all duration-300" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Buscador Section */}
      <div className={`mt-16 py-16 ${
        darkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-purple-50 to-white'
      }`}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {/* Header with Icon */}
            <div className="flex items-center justify-center mb-6">
              <div className={`p-4 rounded-full mr-4 ${
                darkMode ? 'bg-purple-600' : 'bg-purple-100'
              }`}>
                <Search size={32} className={darkMode ? 'text-white' : 'text-purple-600'} />
              </div>
              <h3 className={`text-3xl md:text-4xl font-bold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Buscador de Capítulos
              </h3>
            </div>
            
            <p className={`text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Encuentra y accede directamente a cualquier capítulo específico usando el formato Volumen-Índice
            </p>
            
            {/* Search Card */}
            <div className={`rounded-2xl p-8 shadow-2xl border hover:shadow-3xl transition-all duration-300 ${
              darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'
            }`}>
              
              {/* Search Input Section */}
              <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center mb-6">
                <div className="flex-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search size={20} className={`${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    } transition-colors duration-300`} />
                  </div>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    placeholder="Ejemplo: 11-5"
                    disabled={searchLoading}
                    className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 text-lg font-medium focus:outline-none focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300 ${
                      searchLoading ? 'opacity-50 cursor-not-allowed' : ''
                    } ${
                      darkMode 
                        ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' 
                        : 'bg-gray-50 border-gray-300 text-gray-800 placeholder-gray-500'
                    }`}
                  />
                </div>
                
                <button
                  onClick={handleGlobalSearch}
                  disabled={searchLoading}
                  className={`lg:w-auto px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl ${
                    searchLoading 
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed shadow-none transform-none' 
                      : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white'
                  }`}
                >
                  {searchLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      <span>Buscando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Search size={20} className="mr-2" />
                      <span>Buscar Capítulo</span>
                    </div>
                  )}
                </button>
              </div>
              
              {/* Error Message with Enhanced Styling */}
              {searchError && (
                <div className={`mb-6 p-4 rounded-xl border-l-4 border-red-500 animate-in slide-in-from-top-2 duration-300 ${
                  darkMode ? 'bg-red-900/20 border-red-400' : 'bg-red-50 border-red-500'
                }`}>
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className={`h-5 w-5 ${
                        darkMode ? 'text-red-400' : 'text-red-400'
                      }`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className={`font-medium ${
                        darkMode ? 'text-red-300' : 'text-red-800'
                      }`}>{searchError}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Enhanced Instructions */}
              <div className={`grid md:grid-cols-2 gap-6 text-sm ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <div className={`p-4 rounded-xl ${
                  darkMode ? 'bg-gray-800/50' : 'bg-purple-50'
                }`}>
                  <div className="flex items-center mb-2">
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      darkMode ? 'bg-purple-400' : 'bg-purple-500'
                    }`}></div>
                    <span className="font-semibold">Formato</span>
                  </div>
                  <p>Volumen-Índice</p>
                  <p className={`text-xs mt-1 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>Ejemplo: 11-5 (Volumen 11, Capítulo 5)</p>
                </div>
                
                <div className={`p-4 rounded-xl ${
                  darkMode ? 'bg-gray-800/50' : 'bg-purple-50'
                }`}>
                  <div className="flex items-center mb-2">
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      darkMode ? 'bg-green-400' : 'bg-green-500'
                    }`}></div>
                    <span className="font-semibold">Rango Disponible</span>
                  </div>
                  <p>Volúmenes 1-36</p>
                  <p className={`text-xs mt-1 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>Índices desde 1 en adelante</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Donation Section */}
      <div className={`mt-16 py-12 ${
        darkMode ? 'bg-purple-900' : 'bg-purple-100'
      }`}>
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${
              darkMode ? 'text-white' : 'text-purple-900'
            }`}>
              Ayúdanos a desarrollar esta plataforma de Libro de Cielo
            </h3>
            <p className={`text-lg mb-8 ${
              darkMode ? 'text-purple-100' : 'text-purple-800'
            }`}>
              Tu apoyo nos permite continuar compartiendo las enseñanzas de la Divina Voluntad con el mundo
            </p>
            <a
              href="https://donorbox.org/divinavoluntadapp"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                darkMode 
                  ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg hover:shadow-xl' 
                  : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              <svg 
                className="w-6 h-6 mr-3" 
                fill="currentColor" 
                viewBox="0 0 20 20" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
              </svg>
              Dona aquí
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`mt-16 py-8 ${
        darkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-gray-200 border-t border-gray-300'
      }`}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">
            © {new Date().getFullYear()} Fundación Hijos de la Luz, Inc. Todos los derechos reservados.
          </p>
          {!isAuthenticated && (
            <div className="mt-4 space-x-4">
              <Link
                to="/login"
                className="text-sm text-purple-600 hover:text-purple-500 transition-colors duration-300"
              >
                Acceso de administrador
              </Link>
            </div>
          )}
        </div>
      </footer>
      
      {/* About Modal */}
      <AboutModal 
        isOpen={aboutModalOpen} 
        onClose={() => setAboutModalOpen(false)} 
      />
    </div>
  );
};

export default HomePage;